/**
 * Iconos de tipo de vehículo para el filtro de VehicleGrid2.
 *
 * Antes esto apuntaba a imágenes alojadas en sarret.cl (el WordPress de otra
 * automotora) y en static.wixstatic.com. Esas URLs empezaron a devolver 404 y
 * el filtro quedó mostrando el icono de imagen rota en todos los sitios que
 * usan el bloque. Ahora los iconos son SVG embebidos: no salen a la red, no
 * dependen de terceros y no se pueden romper.
 *
 * Son siluetas de trazo blanco pensadas para el fondo oscuro del bloque, con
 * el mismo tamaño de caja que las imágenes anteriores (max-h-14 / object-contain).
 */

const svg = (body: string): string =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 32" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`
  )}`;

/** Ruedas + los tramos de piso que quedan entre ellas, para no cruzar el neumático. */
const rolling = (x1: number, x2: number, r: number, left = 4, right = 60): string =>
  `<circle cx="${x1}" cy="23" r="${r}"/><circle cx="${x2}" cy="23" r="${r}"/>` +
  `<path d="M${left} 23H${x1 - r}M${x1 + r} 23H${x2 - r}M${x2 + r} 23H${right}"/>`;

const ICONS = {
  sedan: svg(
    `<path d="M4 23v-2.4c0-1.3.9-2.4 2.2-2.7L16.5 15.8 23 10.2c1-.8 2.2-1.2 3.5-1.2h10.3c1.3 0 2.6.4 3.6 1.2l6.6 5.6 8.8 1.9c2.6.6 4.2 1.9 4.2 3.6V23"/>` +
      `<path d="M23.5 15.8l4-4.4h8.5l4.5 4.4M31.5 11.4v4.4M16.5 15.8h31.3"/>` +
      rolling(16, 48, 5)
  ),

  suv: svg(
    `<path d="M4 23v-6.5c0-1.4 1-2.6 2.4-2.9L15.5 12l6-4.4c1-.7 2.1-1.1 3.3-1.1h13c1.3 0 2.5.4 3.5 1.2l6 4.3 8.3 1.8c2.5.5 4 1.8 4 3.5V23"/>` +
      `<path d="M22 12l4.2-3.6h11.5l4.6 3.6M31.5 8.4V12M15.5 12h27.5"/>` +
      rolling(17, 47, 5.5)
  ),

  // Cola corta y vertical: a propósito es más corto que el sedán, si no se confunden.
  hatchback: svg(
    `<path d="M4 23v-3c0-1.3.9-2.4 2.2-2.7L16 15.3l6.4-5.3c1-.8 2.2-1.2 3.5-1.2h13c1.4 0 2.7.5 3.7 1.4l6.5 5.8c.7.6 1.1 1.4 1.1 2.3V23"/>` +
      `<path d="M23 15.3l4.4-4.2h9.6l4.8 4.2M32 11.1v4.2M16 15.3h27"/>` +
      rolling(15, 41, 5, 4, 50)
  ),

  wagon: svg(
    `<path d="M4 23v-3.2c0-1.3.9-2.4 2.2-2.7L15 15.4l6-5c1-.8 2.2-1.2 3.5-1.2h19c1.5 0 2.8.5 3.8 1.5l4 4.1 4.5 1c2.4.6 3.8 1.8 3.8 3.4V23"/>` +
      `<path d="M22 15.4l4.2-4h17.3l4 4M31 11.4v4M38 11.4v4M15 15.4h32"/>` +
      rolling(16, 47, 5)
  ),

  pickup: svg(
    `<path d="M4 23v-3c0-1.3.9-2.4 2.2-2.7L14 15.2l6-5c1-.8 2.2-1.2 3.5-1.2H33c1.3 0 2.6.5 3.6 1.4l5.4 5.2"/>` +
      `<path d="M38.5 16.8H60V23M14 15.2h24"/>` +
      `<path d="M21 15.2l4-4h7.5l4.3 4M28.5 11.2v4"/>` +
      rolling(16, 48, 5.2)
  ),

  van: svg(
    `<path d="M4 23V12c0-1.7 1.3-3 3-3h33c1.2 0 2.3.5 3.1 1.4l6.6 7.3c.6.7 1.3 1 2.1 1.2l3.6.8c1.6.4 2.6 1.5 2.6 3V23"/>` +
      `<path d="M38.8 12.2h4.4l4.2 4.8h-8.6zM9 17.4h29"/>` +
      rolling(16, 46, 5)
  ),

  minivan: svg(
    `<path d="M4 23v-4c0-1.4 1-2.6 2.4-2.9L14 14.6l7-5.6c1-.8 2.3-1.2 3.6-1.2h16c1.4 0 2.8.5 3.9 1.5l5.8 5.5 3.1.7c2.4.5 3.6 1.7 3.6 3.4V23"/>` +
      `<path d="M22.5 14.6l4.6-4.4h16.5l4.6 4.4M33 10.2v4.4M14 14.6h33"/>` +
      rolling(16, 47, 5)
  ),

  coupe: svg(
    `<path d="M4 23v-2.6c0-1.3.9-2.4 2.2-2.7L17 15.4c6.5-4.6 11-6.6 17.5-6.6 5.2 0 8.5 1.4 12.3 4.6l7.3 3.2c2.3 1 3.9 2.2 3.9 3.9V23"/>` +
      `<path d="M24 12.6c4-2.4 7-3.2 11-3.2 3.4 0 5.5.8 8 2.6M17 15.4h29"/>` +
      rolling(16, 47, 5)
  ),

  convertible: svg(
    `<path d="M4 23v-2.6c0-1.3.9-2.4 2.2-2.7L17 15.6h30l7.5 1.9c2.4.6 3.5 1.8 3.5 3.4V23"/>` +
      `<path d="M24.5 15.6l3.8-5.2M40.5 15.6L38.6 12"/>` +
      rolling(16, 47, 5)
  ),

  sport: svg(
    `<path d="M3 23v-1.8c0-1.4 1-2.5 2.4-2.8L15 16.5c7-4.4 12-6.2 19-6.2 5 0 8.7 1.2 12.6 3.7l8.4 3.4c2 .8 3.6 1.8 3.6 3.6V23"/>` +
      `<path d="M23 14.4c4.4-2.4 7.6-3.2 11.6-3.2 3.2 0 5.4.7 7.7 2.2M15 16.5h27"/>` +
      rolling(15, 48, 4.6, 3, 59)
  ),

  citycar: svg(
    `<path d="M9 23v-4.6c0-1.3.9-2.4 2.2-2.7L18 14.4l4.6-4.6c1-1 2.2-1.5 3.6-1.5h11c1.4 0 2.7.5 3.7 1.5l4.6 4.6 3.3.8c1.6.4 2.6 1.5 2.6 3V23"/>` +
      `<path d="M23.4 14.4l4-3.8h9.6l4 3.8M31.6 10.6v3.8M18 14.4h25"/>` +
      rolling(18, 45, 5, 9, 52)
  ),

  moto: svg(
    `<circle cx="12" cy="21.5" r="7.5"/><circle cx="52" cy="21.5" r="7.5"/>` +
      `<path d="M12 21.5 18 14h12l4.5 5.5"/>` + // chasis y motor
      `<path d="M16.5 14h12"/>` + // asiento
      `<path d="M34.5 19.5 43 11.5"/>` + // horquilla
      `<path d="M39.5 10h7"/>` + // manillar
      `<path d="M43 11.5 52 21.5"/>` // eje delantero
  ),

  truck: svg(
    `<path d="M3 23V8h33v15"/>` +
      `<path d="M36 23V13h6.6l6 5.4 4.6 1c2 .4 3.4 1.7 3.4 3.4V23"/>` +
      `<path d="M39.4 13.8h3.4l4.2 3.9h-7.6z"/>` +
      `<circle cx="13" cy="23" r="4.6"/><circle cx="30" cy="23" r="4.6"/><circle cx="50" cy="23" r="4.6"/>` +
      `<path d="M3 23h5.4M17.6 23h7.8M34.6 23h10.8M54.6 23H58"/>`
  ),

  bus: svg(
    `<path d="M3 23V10c0-1.7 1.3-3 3-3h49c1.7 0 3 1.3 3 3v13"/>` +
      `<path d="M8 11.5h44v5.5H8zM22 11.5V17M36 11.5V17"/>` +
      rolling(15, 47, 4.6, 3, 58)
  ),

  boat: svg(
    `<path d="M5 17.5h50l-4.6 6.2c-.9 1.2-2.2 1.8-3.7 1.8H13.3c-1.5 0-2.8-.6-3.7-1.8z"/>` +
      `<path d="M19 17.5v-5.2c0-.9.7-1.6 1.6-1.6H30l6.5 6.8"/>` +
      `<path d="M3 28.6c2.6 1.3 5.2 1.3 7.8 0s5.2-1.3 7.8 0 5.2 1.3 7.8 0 5.2-1.3 7.8 0 5.2 1.3 7.8 0 5.2-1.3 7.8 0 5.2 1.3 7.8 0"/>`
  ),

  machinery: svg(
    `<path d="M8 20v-4.6h13.5V10h9.5l3.2 5.4h6.2V20"/>` +
      `<path d="M33 11.5l9.5-5.2 5.6 3-7.4 8.6"/>` +
      `<path d="M7 22h32a3.5 3.5 0 0 1 0 7H7a3.5 3.5 0 0 1 0-7z"/>` +
      `<circle cx="12.5" cy="25.5" r="1.6"/><circle cx="23" cy="25.5" r="1.6"/><circle cx="33.5" cy="25.5" r="1.6"/>`
  ),
} as const;

type IconKey = keyof typeof ICONS;

/** El icono que se usa cuando la categoría no está en el mapa. */
const FALLBACK: IconKey = 'sedan';

/**
 * Categorías reales de la tabla `categories`, normalizadas (mayúsculas y sin
 * acentos). Lo que no esté aquí cae en FALLBACK, nunca en una imagen rota.
 */
const BY_CATEGORY: Record<string, IconKey> = {
  // Autos
  AUTOMOVIL: 'sedan',
  SEDAN: 'sedan',
  HATCHBACK: 'hatchback',
  'CITY CAR': 'citycar',
  COUPE: 'coupe',
  CLASICO: 'coupe',
  LIMUSINA: 'sedan',
  CONVERTIBLE: 'convertible',
  DEPORTIVO: 'sport',
  'STATION WAGON': 'wagon',
  WAGON: 'wagon',
  SUV: 'suv',
  CROSSOVER: 'suv',

  // Camionetas y comerciales livianos
  CAMIONETA: 'pickup',
  PICKUP: 'pickup',
  FURGON: 'van',
  VAN: 'van',
  MINIVAN: 'minivan',
  MOTORHOME: 'minivan',
  AMBULANCIA: 'van',

  // Motos y similares
  MOTO: 'moto',
  'MOTO, ATV Y UTV': 'moto',
  CUATRICICLO: 'moto',
  TRICICLO: 'moto',
  BUGGY: 'moto',
  KART: 'moto',

  // Camiones
  CAMION: 'truck',
  'CAMION ARTICULADO': 'truck',
  'CAMION CISTERNA': 'truck',
  'CAMION FRIGORIFICO': 'truck',
  'CAMION PLATAFORMA': 'truck',
  'CAMION PLUMA': 'truck',
  'CAMION PORTACONTENEDORES': 'truck',
  'CAMION RIGIDO': 'truck',
  'CAMION TOLVA': 'truck',
  TRACTOCAMION: 'truck',
  CABEZAL: 'truck',

  // Buses
  BUS: 'bus',
  MINIBUS: 'bus',
  'MICRO BUS': 'bus',

  // Náutica
  LANCHA: 'boat',
  'LANCHA DE PESCA': 'boat',
  BOTE: 'boat',
  'BOTE INFLABLE': 'boat',
  SEMIRRIGIDO: 'boat',
  PONTON: 'boat',
  CATAMARAN: 'boat',
  CRUCERO: 'boat',
  VELERO: 'boat',
  YATE: 'boat',
  KAYAK: 'boat',
  'JET SKI': 'moto',
  'MOTO DE AGUA': 'moto',
  'MOTOR FUERA DE BORDA': 'boat',

  // Maquinaria
  MAQUINARIA: 'machinery',
  RETROEXCAVADORA: 'machinery',
  EXCAVADORA: 'machinery',
  'MINI EXCAVADORA': 'machinery',
  BULLDOZER: 'machinery',
  'CARGADOR FRONTAL': 'machinery',
  MINICARGADOR: 'machinery',
  MOTONIVELADORA: 'machinery',
  'RODILLO COMPACTADOR': 'machinery',
  GRUA: 'machinery',
  MONTACARGAS: 'machinery',
  'MANIPULADOR TELESCOPICO': 'machinery',
  'PLATAFORMA ELEVADORA': 'machinery',
  'TRACTOR AGRICOLA': 'machinery',
  COSECHADORA: 'machinery',
  GENERADOR: 'machinery',
  COMPRESOR: 'machinery',
};

/** Rango de marcas diacríticas combinantes que deja NFD (U+0300..U+036F). */
const COMBINING_START = 0x0300;
const COMBINING_END = 0x036f;

const normalize = (value: string): string => {
  const sinAcentos = value
    .normalize('NFD')
    .split('')
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code < COMBINING_START || code > COMBINING_END;
    })
    .join('');

  return sinAcentos.replace(/\s+/g, ' ').trim().toUpperCase();
};

/**
 * Devuelve el data-URI del icono para una categoría. Siempre devuelve algo:
 * si la categoría no está mapeada usa la silueta genérica.
 */
export const getVehicleTypeIcon = (typeName: string | null | undefined): string => {
  const key = normalize(String(typeName ?? ''));
  return ICONS[BY_CATEGORY[key] ?? FALLBACK];
};
